import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Loader2, Star, Car, User, Calendar, Award, MessageSquare } from 'lucide-react';
import axios from 'axios';

const DriverProfilePage = () => {
  const navigate = useNavigate();
  const { driverId } = useParams();
  const logoUrl = 'https://customer-assets.emergentagent.com/job_transport-app-29/artifacts/c2qih6ut_J%20%26%20S%20Logo%2001%20%20%282%29_1024x1024.jpg';
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [driver, setDriver] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchDriverProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/drivers/${driverId}/reviews`);
      setDriver(response.data.driver);
      setReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching driver profile:', error);
    } finally {
      setIsLoading(false);
    }
  }, [BACKEND_URL, driverId]);

  useEffect(() => {
    if (driverId) {
      fetchDriverProfile();
    }
  }, [driverId, fetchDriverProfile]);

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      if (review.rating) {
        distribution[review.rating]++;
      }
    });
    return distribution;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50 flex items-center justify-center">
        <Loader2 className="h-16 w-16 text-amber-500 animate-spin" />
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
        <div className="container mx-auto px-4 py-16 text-center">
          <h2 className="text-2xl font-bold mb-2">Driver Not Found</h2>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    );
  }

  const ratingDistribution = getRatingDistribution();
  const maxRatingCount = Math.max(...Object.values(ratingDistribution), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-amber-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft size={20} />
          </Button>
          <img src={logoUrl} alt="Logo" className="h-10 w-10 object-contain" />
          <span className="text-xl font-bold bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">Driver Profile</span>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {/* Driver Card */}
        <Card className="p-6 border-2 mb-6">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
              <User className="text-white" size={40} />
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold">{driver.name}</h2>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="fill-amber-400 text-amber-400" size={20} />
                  <span className="font-bold text-lg">{driver.rating || '0.0'}</span>
                </div>
                <span className="text-gray-500">({driver.total_ratings || 0} ratings)</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-3 bg-gray-50 rounded-lg">
              <Car className="mx-auto text-amber-600 mb-1" size={24} />
              <p className="text-xl font-bold">{driver.total_rides || 0}</p>
              <p className="text-xs text-gray-600">Total Rides</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <Award className="mx-auto text-amber-600 mb-1" size={24} />
              <p className="text-xl font-bold">{driver.rating || '0.0'}</p>
              <p className="text-xs text-gray-600">Rating</p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <MessageSquare className="mx-auto text-amber-600 mb-1" size={24} />
              <p className="text-xl font-bold">{driver.total_ratings || 0}</p>
              <p className="text-xs text-gray-600">Reviews</p>
            </div>
          </div>
        </Card>

        {/* Rating Distribution */}
        <Card className="p-6 border-2 mb-6">
          <h3 className="font-semibold text-lg mb-4">Rating Distribution</h3>
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => (
              <div key={stars} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-12">
                  <span className="text-sm font-medium">{stars}</span>
                  <Star size={14} className="fill-amber-400 text-amber-400" />
                </div>
                <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-400 rounded-full transition-all"
                    style={{ width: `${(ratingDistribution[stars] / maxRatingCount) * 100}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600 w-8 text-right">{ratingDistribution[stars]}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Reviews */}
        <Card className="p-6 border-2">
          <h3 className="font-semibold text-lg mb-4">Recent Reviews</h3>
          {reviews.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare size={32} className="mx-auto mb-2 opacity-50" />
              <p>No reviews yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review, index) => (
                <div key={index} className="border-b pb-4 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <User size={16} className="text-gray-600" />
                      </div>
                      <span className="font-medium">{review.passenger_name || 'Passenger'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star 
                          key={star}
                          size={14}
                          className={star <= review.rating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}
                        />
                      ))}
                    </div>
                  </div>
                  {review.rating_comment && (
                    <p className="text-gray-700 text-sm ml-10">{review.rating_comment}</p>
                  )}
                  {review.rated_at && (
                    <p className="text-xs text-gray-400 ml-10 mt-1">
                      {new Date(review.rated_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DriverProfilePage;
